import { observable, computed, action } from 'mobx';
import moment from 'moment';
import BaseQueryParamsStore from 'shared/stores/BaseQueryParamsStore';

import { MIN_DATE, MAX_DATE, TRANSPORT_DATE_FORMAT } from 'shared/config/widget';

import lightTheme from 'shared/themes/light';
import darkTheme from 'shared/themes/dark';

export const THEME = {
  LIGHT: 'light',
  DARK: 'dark'
}

export const TREE_TYPE = {
  TNVED: 'tnved',
  GNG: 'gng',
  ETSNG: 'etsng'
}

export const DENSE = {
  TRUE: 'true',
  FALSE: 'false'
}

export const SEARCH_OPEN = {
  TRUE: 'true',
  FALSE: 'false'
}

export const CLOSE_TYPE = {
  BACK: 'back',
  CLOSE: 'close'
}

export const DISABLE_INFO = {
  TRUE: 'true',
  FALSE: 'false'
}

export const DIRECTION_CUSTOMS = {
  COMMON: '0',
  EXPORT: '1',
  IMPORT: '2'
}

export const CLEARANCE_COUNTRIES = {
  ALL: 'Все страны',
  RU: 'Россия',
  BY: 'Беларусь',
  KZ: 'Казахстан',
  AM: 'Армения',
  KG: 'Киргизия',
}

export const DEFAULT_SEARCH_TERM = '';

// export const ORIGIN_COUNTRIES = {};
export const ORIGIN_COUNTRIES = [];

const EXPANDED_NODES_SEPARATOR = ';';

const DEFAULT_QUERY_PARAMS = {
  apiKey: null,
  theme: THEME.LIGHT,
  type: TREE_TYPE.TNVED,
  dense: DENSE.FALSE,
  date: null,
  infoCode: null,
  searchTerm: DEFAULT_SEARCH_TERM,
  searchOpen: SEARCH_OPEN.FALSE,
  expanded: '',
  closeType: CLOSE_TYPE.CLOSE,
  disableInfo: DISABLE_INFO.FALSE,
  dominantMode: false,
  direction: DIRECTION_CUSTOMS.IMPORT,
  clearanceCountry: 'RU',
  originCountry: 'ALL',
  treeLink: null,
  treeLinkTarget: null,
  isBlink: false
}

export default class QueryParamsStore extends BaseQueryParamsStore {

  DEFAULT_QUERY_PARAMS = DEFAULT_QUERY_PARAMS;

  @observable queryParams = DEFAULT_QUERY_PARAMS;

  @computed
  get apiKey() {
    if (this.queryParams.apiKey === null) {
      return process.env.REACT_APP_API_KEY;
    }
    else {
      return this.queryParams.apiKey;
    }
  }

  @computed
  get dominantMode() {
    return this.queryParams.dominantMode;
  }

  @computed
  get theme() {
    let theme = lightTheme;

    if (this.queryParams.theme === THEME.DARK) {
      theme = darkTheme;
    }

    return theme;
  }

  @computed
  get treeStore() {
    const { type } = this.queryParams;
    const { tnvedTreeStore, gngTreeStore, etsngTreeStore } = this.rootStore;
    let result;

    switch (type) {
      case TREE_TYPE.TNVED: result = tnvedTreeStore; break;
      case TREE_TYPE.ETSNG: result = etsngTreeStore; break;
      case TREE_TYPE.GNG: result = gngTreeStore; break;
      default:
        result = tnvedTreeStore;
    }

    return result;
  }

  @computed
  get dense() {
    return this.queryParams.dense === DENSE.TRUE;
  }

  @computed
  get date() {
    const { date } = this.queryParams;
    return date ? moment(date, TRANSPORT_DATE_FORMAT) : null;
  }
  set date(value) {
    this.queryParams.date = value ? value.format(TRANSPORT_DATE_FORMAT) : null;
    this.updateUrl();
  }

  @computed
  get direction() {
    return this.queryParams.direction;
  }
  set direction(value) {
    this.queryParams.direction = value;
  }

  @computed
  get clearanceCountry() {
    return this.queryParams.clearanceCountry;
  }
  set clearanceCountry(value) {
    this.queryParams.clearanceCountry = value;
  }

  @computed
  get originCountry() {
    return this.queryParams.originCountry;
  }
  set originCountry(value) {
    this.queryParams.originCountry = value;
  }

  @computed
  get infoCode() {
    return this.queryParams.infoCode;
  }
  set infoCode(value) {
    this.queryParams.infoCode = value;
    this.updateUrl();
  }

  @computed
  get treeLink() {
    return this.queryParams.treeLink;
  }
  set treeLink(value) {
    this.queryParams.treeLink = value;
  }

  @computed
  get treeLinkTarget() {
    return this.queryParams.treeLinkTarget;
  }
  set treeLinkTarget(value) {
    this.queryParams.treeLinkTarget = value;
  }

  @computed
  get isBlink() {
    return this.queryParams.isBlink;
  }
  set isBlink(value) {
    this.queryParams.isBlink = value;
  }

  @computed
  get searchOpen() {
    return this.queryParams.searchOpen === SEARCH_OPEN.TRUE;
  }
  set searchOpen(value) {
    this.queryParams.searchOpen = !!value ? SEARCH_OPEN.TRUE : SEARCH_OPEN.FALSE;
    this.updateUrl();
  }

  @computed
  get searchTerm() {
    return decodeURI(this.queryParams.searchTerm) || DEFAULT_SEARCH_TERM;
  }
  set searchTerm(value = DEFAULT_SEARCH_TERM) {
    this.queryParams.searchTerm = value;
    this.updateUrl();
  }

  @computed
  get expanded() {
    const expanded = this.queryParams.expanded;
    return expanded ? expanded.split(EXPANDED_NODES_SEPARATOR) : [];
  }
  set expanded([...nodePaths]) {
    this.queryParams.expanded = nodePaths.join(EXPANDED_NODES_SEPARATOR);
    this.updateUrl();
  }

  @computed
  get closeType() {
    const { closeType } = this.queryParams;
    const isClose = closeType === CLOSE_TYPE.CLOSE;
    const isBack = closeType === CLOSE_TYPE.BACK;
    return { isClose, isBack };
  }

  @computed
  get disableInfo() {
    return this.queryParams.disableInfo === DISABLE_INFO.TRUE;
  }

  @action.bound
  extractQueryParams() {
    const currentExpanded = this.expanded;
    const queryParams = super.extractQueryParams();

    this.extractRawParam(queryParams, 'apiKey', null);
    this.extractRawParam(queryParams, 'dominantMode', false);
    this.extractDictionaryParam(queryParams, 'theme', THEME);
    this.extractDictionaryParam(queryParams, 'type', TREE_TYPE);
    this.extractDictionaryParam(queryParams, 'dense', DENSE);
    this.extractDateParam(queryParams, 'date', TRANSPORT_DATE_FORMAT, MIN_DATE, MAX_DATE);
    this.extractRawParam(queryParams, 'searchTerm', DEFAULT_SEARCH_TERM);
    this.extractDictionaryParam(queryParams, 'searchOpen', SEARCH_OPEN);
    this.extractRawParam(queryParams, 'infoCode', null);
    this.extractRawParam(queryParams, 'direction', this.direction === undefined ? DIRECTION_CUSTOMS.IMPORT : this.direction);
    this.extractRawParam(queryParams, 'clearanceCountry', this.clearanceCountry === undefined ? 'RU' : this.clearanceCountry);
    this.extractRawParam(queryParams, 'originCountry', this.originCountry === undefined ? 'ALL' : this.originCountry);
    this.extractRawParam(queryParams, 'expanded', '');
    this.extractDictionaryParam(queryParams, 'closeType', CLOSE_TYPE);
    this.extractDictionaryParam(queryParams, 'disableInfo', DISABLE_INFO);
    this.extractRawParam(queryParams, 'isBlink', false);
    this.extractRawParam(queryParams, 'treeLink', null);
    this.extractRawParam(queryParams, 'treeLinkTarget', null);

    if (currentExpanded !== this.expanded && this.treeStore) {
      this.treeStore.restoreTreeState();
    }

    return queryParams;
  }

}



// WEBPACK FOOTER //
// ./src/stores/QueryParamsStore.js